'use client'

import { useEffect, JSX } from 'react'

import * as Sentry from '@sentry/nextjs'
import { useQueryClient } from '@tanstack/react-query'
import { CircleAlert, RefreshCwIcon } from 'lucide-react'
import { NextPageContext } from 'next'
import NextErrorComponent, { ErrorProps as NextErrorProps } from 'next/error'

import { persister } from '~/app/_root/QueryClientProvider'
import { SplitsLink as Link } from '~/components/Link'
import { Button, EmptyState, PageLayout } from '~/components/component-library'

export type ErrorPageProps = {
  error?: Error
  err?: Error
  statusCode?: number
  isReadyToRender?: boolean
  children?: React.ReactElement
}

export type ErrorProps = {
  isReadyToRender: boolean
} & NextErrorProps

const ErrorPage = (props: ErrorPageProps): JSX.Element => {
  const {
    statusCode,
    isReadyToRender,
    err: serverError,
    error: clientError,
  } = props

  const activeError = clientError || serverError

  const queryClient = useQueryClient()

  useEffect(() => {
    if (clientError) {
      Sentry.captureException(clientError)
    }
  }, [clientError])

  if (process.env.NEXT_PUBLIC_APP_STAGE !== 'development') {
    // eslint-disable-next-line no-console
    console.warn('ErrorPage - Unexpected error caught')
    // eslint-disable-next-line no-console
    console.error(activeError)
  }

  if (!isReadyToRender && serverError) {
    Sentry.captureException(serverError)
  }

  return (
    <PageLayout>
      <EmptyState
        className="mt-32"
        headingLevel={1}
        {...(statusCode
          ? { size: 'page', title: String(statusCode) }
          : {
              icon: <CircleAlert className="h-12 w-12" />,
              title: 'Something went wrong',
            })}
      >
        {!!statusCode && <p>Something went wrong.</p>}
        <p>
          Please try refreshing the page. If the issue persists, feel free to
          reach out to{' '}
          <Link path="mailto:support@splits.org">support@splits.org</Link>.
        </p>
        <div className="inline-block">
          <Button
            variant={'secondary'}
            onClick={() => {
              queryClient.clear() // Clear the in-memory cache
              persister.removeClient() // Clear the localstorage cache
              window.location.reload()
            }}
            suffix={<RefreshCwIcon />}
          >
            Refresh page
          </Button>
        </div>
      </EmptyState>
    </PageLayout>
  )
}

ErrorPage.getInitialProps = async (
  props: NextPageContext,
): Promise<ErrorProps> => {
  const { res, err, asPath } = props

  const errorInitialProps: ErrorProps =
    (await NextErrorComponent.getInitialProps({
      res,
      err,
    } as NextPageContext)) as ErrorProps

  if (process.env.NEXT_PUBLIC_APP_STAGE !== 'production') {
    // eslint-disable-next-line no-console
    console.error('ErrorPage.getInitialProps - Unexpected error caught:', err)
  }

  errorInitialProps.isReadyToRender = true

  if (res?.statusCode === 404) {
    return { statusCode: 404, isReadyToRender: true }
  }

  if (err) {
    Sentry.captureException(err)
    await Sentry.flush(2000)
    return errorInitialProps
  }

  Sentry.captureException(
    new Error(`_error.js getInitialProps missing data at path: ${asPath}`),
  )
  await Sentry.flush(2000)

  return errorInitialProps
}

export default ErrorPage
